import { createContext, useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import { useAuth } from "@/auth/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CartContext = createContext(null);

export const useCart = () => useContext(CartContext);

export function CartProvider({ children }) {
  const { user, token } = useAuth();
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("vf-cart")) || [];
    } catch {
      return [];
    }
  });
  const skipNextSync = useRef(false);
  const [discount, setDiscount] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("vf-cart-discount")) || null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    localStorage.setItem("vf-cart", JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    if (discount) localStorage.setItem("vf-cart-discount", JSON.stringify(discount));
    else localStorage.removeItem("vf-cart-discount");
  }, [discount]);

  // Restore the server-saved cart on login when the local cart is empty
  useEffect(() => {
    if (!user || !token) return;
    axios.get(`${API}/cart`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => {
        if (data.items?.length) {
          setItems((prev) => {
            if (prev.length) return prev;
            skipNextSync.current = true;
            return data.items;
          });
        }
        // Hydrate a discount code saved on the server cart (e.g. applied by VetForge)
        if (data.discount_code && !discount) {
          axios.post(`${API}/discounts/validate`, { code: data.discount_code }, { headers: { Authorization: `Bearer ${token}` } })
            .then(({ data: v }) => setDiscount({ code: v.code, percent: v.percent }))
            .catch(() => {});
        }
      })
      .catch(() => {});
  }, [user, token]);

  // Sync cart changes to the server so they persist across devices and are
  // visible to the owner in the admin dashboard
  useEffect(() => {
    if (!user || !token) return;
    if (skipNextSync.current) {
      skipNextSync.current = false;
      return;
    }
    const t = setTimeout(() => {
      axios.put(`${API}/cart`, { items, discount_code: discount?.code || null }, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    }, 800);
    return () => clearTimeout(t);
  }, [items, discount, user, token]);

  const add = (product, variant = null) => {
    const key = variant ? `${product.id}:${variant.id}` : product.id;
    setItems((prev) => {
      const existing = prev.find((i) => i.id === key);
      if (existing) {
        return prev.map((i) => (i.id === key ? { ...i, quantity: i.quantity + 1 } : i));
      }
      return [...prev, {
        id: key,
        product_id: product.id,
        variant_id: variant?.id || null,
        name: variant ? `${product.liveName || product.name} — ${variant.label}` : (product.liveName || product.name),
        size: variant?.size || product.size,
        price: variant ? variant.price : (product.livePrice ?? product.price),
        image: product.image,
        quantity: 1,
      }];
    });
  };

  const updateQty = (id, delta) =>
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, quantity: Math.max(1, i.quantity + delta) } : i)));

  const remove = (id) => setItems((prev) => prev.filter((i) => i.id !== id));
  const clear = () => {
    setItems([]);
    setDiscount(null);
  };
  const applyDiscount = (d) => setDiscount(d);
  const removeDiscount = () => setDiscount(null);
  const count = items.reduce((sum, i) => sum + i.quantity, 0);

  return (
    <CartContext.Provider value={{ items, add, updateQty, remove, clear, count, discount, applyDiscount, removeDiscount }}>
      {children}
    </CartContext.Provider>
  );
}
